import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Select, Table, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { GroupAnalyticsParams, AiCompetitivenessRow } from '@/types';
import { adminApi } from '@services/api';
import { reportLoadErrorMessage } from '@/utils/apiError';

const { Text } = Typography;

const ALL_BUILDS = 'all';
const UNKNOWN_BUILD = 'unknown';
const NOT_REPORTED_DIFFICULTY = -1;
const MISSING = '—';

const fmtCount = (n: number) => n.toLocaleString('en-IN');

const fmtRate = (v?: number) => (v == null ? MISSING : `${(v * 100).toFixed(1)}%`);

const fmtRating = (v?: number) =>
  v == null
    ? MISSING
    : v.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const fmtAvg = (v?: number) =>
  v == null
    ? MISSING
    : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtPair = (player?: number, ai?: number) =>
  player == null && ai == null ? MISSING : `${fmtAvg(player)} / ${fmtAvg(ai)}`;

const buildLabel = (build: string) => (build === UNKNOWN_BUILD ? 'Not sent' : `Build ${build}`);

// Builds are numeric (smallint on the wire); 'unknown' always sorts last.
const compareBuilds = (a: string, b: string) => {
  if (a === b) return 0;
  if (a === UNKNOWN_BUILD) return 1;
  if (b === UNKNOWN_BUILD) return -1;
  return a.localeCompare(b, undefined, { numeric: true });
};

// "Not reported" (-1) sorts after every real difficulty.
const difficultySortKey = (d: number) =>
  d === NOT_REPORTED_DIFFICULTY ? Number.MAX_SAFE_INTEGER : d;

// Missing values (rates, ratings) sort below every real value.
const missingLowKey = (v?: number) => (v == null ? -1 : v);

// Green = competitive (40-60%), orange = drifting (30-40 / 60-70%), red = off.
const winRateColor = (rate: number) => {
  const pct = rate * 100;
  if (pct >= 40 && pct <= 60) return 'green';
  if ((pct >= 30 && pct < 40) || (pct > 60 && pct <= 70)) return 'orange';
  return 'red';
};

const columns: ColumnsType<AiCompetitivenessRow> = [
  {
    title: 'Build',
    dataIndex: 'buildVersion',
    key: 'buildVersion',
    render: (v: string) =>
      v === UNKNOWN_BUILD ? <Text type="secondary">Not sent</Text> : <Text>{v}</Text>,
  },
  {
    title: 'AI difficulty',
    dataIndex: 'aiDifficulty',
    key: 'aiDifficulty',
    align: 'right',
    sorter: (a, b) => difficultySortKey(a.aiDifficulty) - difficultySortKey(b.aiDifficulty),
    render: (v: number) =>
      v === NOT_REPORTED_DIFFICULTY ? <Text type="secondary">Not reported</Text> : v,
  },
  {
    title: 'Matches',
    dataIndex: 'matches',
    key: 'matches',
    align: 'right',
    sorter: (a, b) => a.matches - b.matches,
    render: (v: number) => fmtCount(v),
  },
  {
    title: 'With result',
    dataIndex: 'matchesWithVerdict',
    key: 'matchesWithVerdict',
    align: 'right',
    sorter: (a, b) => a.matchesWithVerdict - b.matchesWithVerdict,
    render: (v: number) => fmtCount(v),
  },
  {
    title: 'Player win rate',
    dataIndex: 'playerWinRate',
    key: 'playerWinRate',
    align: 'right',
    sorter: (a, b) => missingLowKey(a.playerWinRate) - missingLowKey(b.playerWinRate),
    render: (v: number | undefined, row) =>
      v == null ? (
        <Text type="secondary">{MISSING}</Text>
      ) : (
        <Tooltip title={`Over ${fmtCount(row.matchesWithVerdict)} matches with a result`}>
          <Tag color={winRateColor(v)} style={{ margin: 0 }}>
            {fmtRate(v)}
          </Tag>
        </Tooltip>
      ),
  },
  {
    title: 'Tie rate',
    dataIndex: 'tieRate',
    key: 'tieRate',
    align: 'right',
    sorter: (a, b) => missingLowKey(a.tieRate) - missingLowKey(b.tieRate),
    render: (v?: number) => fmtRate(v),
  },
  {
    title: 'Avg player rating',
    dataIndex: 'avgPlayerRating',
    key: 'avgPlayerRating',
    align: 'right',
    sorter: (a, b) => missingLowKey(a.avgPlayerRating) - missingLowKey(b.avgPlayerRating),
    render: (v?: number) => fmtRating(v),
  },
  {
    title: 'Avg AI rating',
    dataIndex: 'avgOpponentRating',
    key: 'avgOpponentRating',
    align: 'right',
    sorter: (a, b) => missingLowKey(a.avgOpponentRating) - missingLowKey(b.avgOpponentRating),
    render: (v?: number) => fmtRating(v),
  },
  {
    title: 'Fours (player / AI)',
    key: 'fours',
    align: 'right',
    render: (_v, row) => fmtPair(row.avgUserFours, row.avgAiFours),
  },
  {
    title: 'Sixes (player / AI)',
    key: 'sixes',
    align: 'right',
    render: (_v, row) => fmtPair(row.avgUserSixes, row.avgAiSixes),
  },
  {
    title: 'Dot balls (player / AI)',
    key: 'dotBalls',
    align: 'right',
    render: (_v, row) => fmtPair(row.avgUserDotBalls, row.avgAiDotBalls),
  },
];

/**
 * AI competitiveness per client build and AI difficulty.
 *
 * The player's win rate is only computed over matches that reported a
 * result; counting the rest would drag it down and make the AI look harder
 * than it is. Rows are per build so a tuning change reads as a before/after
 * across builds without any release tagging.
 */
export const AiCompetitivenessCard: React.FC<{ params: GroupAnalyticsParams }> = ({ params }) => {
  const [rows, setRows] = useState<AiCompetitivenessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [build, setBuild] = useState<string>(ALL_BUILDS);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAiCompetitiveness(params);
      setRows(res.data ?? []);
      setError(null);
    } catch (e) {
      setError(reportLoadErrorMessage(e, 'Could not load AI competitiveness'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void load();
  }, [load]);

  const builds = useMemo(
    () => Array.from(new Set(rows.map((r) => r.buildVersion))).sort(compareBuilds),
    [rows]
  );

  const buildOptions = useMemo(
    () => [
      { value: ALL_BUILDS, label: 'All builds' },
      ...builds.map((b) => ({ value: b, label: buildLabel(b) })),
    ],
    [builds]
  );

  // A build picked for an earlier range may not exist in the new one.
  const activeBuild = useMemo(
    () => (build !== ALL_BUILDS && builds.includes(build) ? build : ALL_BUILDS),
    [build, builds]
  );

  const visibleRows = useMemo(
    () =>
      rows
        .filter((r) => activeBuild === ALL_BUILDS || r.buildVersion === activeBuild)
        .sort(
          (a, b) =>
            compareBuilds(a.buildVersion, b.buildVersion) ||
            difficultySortKey(a.aiDifficulty) - difficultySortKey(b.aiDifficulty)
        ),
    [rows, activeBuild]
  );

  // Pooled player win rate for the selection, weighted by matches with a result.
  const summary = useMemo(() => {
    let matches = 0;
    let withResult = 0;
    let wins = 0;
    for (const r of visibleRows) {
      matches += r.matches;
      if (r.playerWinRate != null) {
        withResult += r.matchesWithVerdict;
        wins += r.playerWinRate * r.matchesWithVerdict;
      }
    }
    return {
      matches,
      withResult,
      winRate: withResult > 0 ? wins / withResult : undefined,
    };
  }, [visibleRows]);

  return (
    <Card
      loading={loading}
      title={
        <span>
          AI competitiveness{' '}
          <Tooltip title="Player win rate is the player's win rate over matches that reported a result, not over all matches. Around 50% means the AI is competitive; well above means it is too easy, well below means it is too punishing. Rows are per client build, so compare builds to see the effect of a tuning change. Build 'Not sent' means the client sent no build number; difficulty 'Not reported' means the client sent no AI difficulty.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      }
      extra={
        rows.length > 0 && (
          <Select
            size="small"
            value={activeBuild}
            onChange={setBuild}
            options={buildOptions}
            style={{ minWidth: 140 }}
            aria-label="Filter by build"
          />
        )
      }
    >
      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}

      {rows.length === 0 ? (
        !error && (
          <Text type="secondary">
            No AI matches logged in this range. Rows come from the AI analytics log the client posts
            at the end of each match against the AI. Older client builds did not send ratings or dot
            balls, so matches from them show {MISSING} for those columns.
          </Text>
        )
      ) : (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {fmtCount(summary.matches)} matches, {fmtCount(summary.withResult)} with a result.
            Pooled player win rate: {fmtRate(summary.winRate)}.
          </Text>
          <Table<AiCompetitivenessRow>
            size="small"
            rowKey={(r) => `${r.buildVersion}-${r.aiDifficulty}`}
            dataSource={visibleRows}
            columns={columns}
            pagination={{ pageSize: 10, size: 'small', hideOnSinglePage: true }}
            scroll={{ x: 'max-content' }}
          />
        </>
      )}
    </Card>
  );
};
